
import React from "react";
import { RefreshCw, ArrowLeft } from "lucide-react";
import { Spinner, CLS } from "../constants";
import ValidationLog from "./components/ValidationLog";

const STAGES = [
  { key: "understanding", label: "Reading document structure" },
  { key: "indexing",      label: "Indexing into search engine" },
  { key: "generating",    label: "Generating fields & instructions" },
  { key: "validating",    label: "Validating schema" },
  { key: "testing",       label: "Running test extraction" },
];

const STAGE_ORDER = STAGES.map((s) => s.key);

const Step2Build = ({ buildStatus, validationLog, onRetry, onChangeSettings }) => {
  const { status, stage, stage_label, progress, error } = buildStatus || {};
  const isError    = status === "error";
  const isReady    = status === "ready";
  const currentIdx = STAGE_ORDER.indexOf(stage);

  return (
    <div className="flex-1 overflow-y-auto flex justify-center px-6 pt-8 pb-12 bg-[#FEF9F7]">
      <div className="w-full max-w-[640px] flex flex-col gap-5">

        {/* Title */}
        <div>
          <h2 className={`text-base font-bold m-0 mb-1 ${isError ? "text-[#D31C1C]" : "text-[#171717]"}`}>
            {isError ? "Build failed" : isReady ? "Build complete" : "Building your template…"}
          </h2>
          <p className="text-[0.8125rem] text-[#71717A] m-0">
            {isError
              ? "Something went wrong. You can retry or go back to change the settings."
              : "Analysing structure, inferring fields, writing instructions, and running a test extraction."}
          </p>
        </div>

        {/* Progress bar */}
        {!isError && (
          <div>
            <div className="h-1 bg-black/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#8B55DD] to-[#9177B8] rounded-full transition-[width] duration-500 ease-out"
                style={{ width: `${progress || 0}%` }}
              />
            </div>
            {stage_label && (
              <div className="flex items-center gap-2 text-[0.8125rem] text-[#52525B] mt-1.5">
                <Spinner size={13} />
                {stage_label}
              </div>
            )}
          </div>
        )}

        {/* Stage list */}
        <div className="border border-black/[0.08] rounded-[0.625rem] overflow-hidden">
          {STAGES.map((s, i) => {
            let stateClass = "";
            if (isError && i === Math.max(0, currentIdx)) stateClass = "error";
            else if (i < currentIdx || isReady) stateClass = "done";
            else if (i === currentIdx) stateClass = "active";

            const rowColor =
              stateClass === "done" ? "text-[#52525B]" :
              stateClass === "active" ? "text-[#3F3F46] bg-[#8B55DD]/[0.02]" :
              stateClass === "error" ? "text-[#D31C1C]" : "text-[#9CA3AF]";

            const iconColor =
              stateClass === "done" ? "border-[#10B981] bg-[#10B981]/[0.08] text-[#10B981]" :
              stateClass === "active" ? "border-[#8B55DD] bg-[#8B55DD]/[0.06] text-[#8B55DD]" :
              stateClass === "error" ? "border-[#D31C1C] bg-[#D31C1C]/[0.06] text-[#D31C1C]" :
              "border-black/10 text-[#9CA3AF]";

            return (
              <div
                key={s.key}
                className={`flex items-start gap-3 px-4 py-3 bg-white border-b border-black/[0.04] last:border-b-0 text-[0.8125rem] transition-colors duration-150 ${rowColor}`}
              >
                <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[0.6875rem] flex-shrink-0 mt-0.5 ${iconColor}`}>
                  {stateClass === "done"
                    ? "✓"
                    : stateClass === "error"
                    ? "✗"
                    : stateClass === "active"
                    ? <Spinner size={11} />
                    : <span className="text-[#D1D5DB]">{i + 1}</span>
                  }
                </div>
                <div className="flex-1">
                  <div>{s.label}</div>
                  {s.key === "validating" && validationLog?.length > 0 && (
                    <ValidationLog lines={validationLog} />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Error state */}
        {isError && (
          <>
            <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-[0.8125rem] leading-[1.5] bg-[#D31C1C]/[0.06] border border-[#D31C1C]/20 text-[#991B1B]">
              <span className="flex-shrink-0">✗</span>
              <div>
                <strong>Build failed — </strong>
                {error || "Document understanding failed. This sometimes happens with scanned or encrypted files."}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button className={`${CLS.btn} ${CLS.btnGhost}`} onClick={onChangeSettings}>
                <ArrowLeft size={13} /> Change settings
              </button>
              <button className={`${CLS.btn} ${CLS.btnPrimary}`} onClick={onRetry}>
                <RefreshCw size={13} /> Try again
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Step2Build;
